// Case list — search + filter (archived toggle) + inline create form.

import { useState } from "react";
import { Link } from "react-router-dom";

import { cases as casesApi, ApiError } from "../lib/api";
import { useAsync } from "../hooks/useAsync";
import { pushToast } from "../components/Toast";

export function CaseListPage(): JSX.Element {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftType, setDraftType] = useState("");
  const { data, loading, error, refresh } = useAsync(
    () => casesApi.list({ include_archived: includeArchived, limit: 100 }),
    [includeArchived],
  );

  const onCreate = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!draftName.trim()) return;
    setCreating(true);
    try {
      const c = await casesApi.create({
        name: draftName.trim(),
        case_type: draftType.trim() || undefined,
      });
      setDraftName("");
      setDraftType("");
      pushToast("success", `Case #${c.id} created`);
      refresh();
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Failed to create Case");
    } finally {
      setCreating(false);
    }
  };

  const filtered = (data?.cases ?? []).filter((c) =>
    query.trim() === "" ? true : c.name.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Cases</h1>
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-2 text-slate-700">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Show archived
          </label>
        </div>
      </div>

      <section className="card mb-6 p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Create a new Case</h2>
        <form onSubmit={onCreate} className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr,1fr,auto]">
          <input
            aria-label="Case name"
            placeholder="e.g. Smith Workers' Comp dispute"
            className="input"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            required
          />
          <input
            aria-label="Case type"
            placeholder="Type (clinical / legal / mixed)"
            className="input"
            value={draftType}
            onChange={(e) => setDraftType(e.target.value)}
          />
          <button type="submit" className="btn-primary" disabled={creating}>
            {creating ? "Creating…" : "Create"}
          </button>
        </form>
      </section>

      <section className="card p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <input
            aria-label="Filter cases by name"
            placeholder="Filter cases…"
            className="input max-w-xs"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="button" onClick={refresh} className="btn-secondary">
            Refresh
          </button>
        </div>
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {error && <p className="text-sm text-red-700">Could not load Cases: {error.message}</p>}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-slate-600">No Cases match.</p>
        )}
        {filtered.length > 0 && (
          <ul role="list" className="divide-y divide-slate-200">
            {filtered.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3">
                <div>
                  <Link
                    to={`/cases/${c.id}`}
                    className="font-medium text-slate-900 hover:text-brand-700"
                  >
                    {c.name}
                  </Link>
                  {c.archived_at && (
                    <span className="ml-2 badge-yellow">archived</span>
                  )}
                  <div className="text-xs text-slate-500">
                    {c.case_type ? `${c.case_type} · ` : ""}
                    {c.document_count} document{c.document_count === 1 ? "" : "s"} · created{" "}
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                </div>
                <Link to={`/cases/${c.id}`} className="btn-secondary">
                  Open
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
